import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MODULE_KEYS, toggleModuleKeys } from '@heyroot/contracts/tenants/entitlements/module-keys';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import {
  replaceVersionModules,
  queryKeys,
  AdminApiError,
  type BusinessTypeVersion,
} from '../../lib/api';

type ModuleRow = { moduleKey: string; isRecommended: boolean };
type ModuleState = 'off' | 'optional' | 'recommended';

function stateOf(enabled: Map<string, boolean>, key: string): ModuleState {
  if (!enabled.has(key)) return 'off';
  return enabled.get(key) ? 'recommended' : 'optional';
}

const KNOWN = new Set<string>(MODULE_KEYS as readonly string[]);

export function ModuleSetEditor({
  slug,
  version,
  isDraft,
  issues,
}: {
  slug: string;
  version: BusinessTypeVersion;
  isDraft: boolean;
  issues: { code: string; message: string }[];
}) {
  const qc = useQueryClient();
  // Union of MODULE_KEYS and whatever this version actually carries -- dropping
  // an unknown key from the picker would silently delete it on the next save,
  // because the PUT is replace-set (spec §6.2, §9.4: display, do not migrate).
  const allKeys = useMemo(() => {
    // Only the MODULE_KEYS source drops a platform record (`regulated_profile`, #15687: it is
    // recorded on the tenant page, not offered as a template toggle). A key the version already
    // carries is never dropped, or the replace-set save would delete it.
    const s = new Set<string>(toggleModuleKeys());
    for (const m of version.modules) s.add(m.moduleKey);
    // The version's SAVED modules first, then everything else alphabetically.
    // The registry has ~48 keys and a typical template enables a handful, so a
    // flat alphabetical grid scatters the few that matter among forty that do
    // not, with no at-a-glance view of the set being edited.
    //
    // Ordered by the saved set (props) rather than the live `enabled` state on
    // purpose: keying it off local state would make a row jump to the top the
    // instant you clicked it, moving the thing under the cursor.
    const saved = new Set(version.modules.map((m) => m.moduleKey));
    return [...s].sort((a, b) => {
      const aOn = saved.has(a);
      const bOn = saved.has(b);
      if (aOn !== bOn) return aOn ? -1 : 1;
      return a.localeCompare(b);
    });
  }, [version.modules]);

  const [enabled, setEnabled] = useState<Map<string, boolean>>(
    () => new Map(version.modules.map((m) => [m.moduleKey, m.isRecommended])),
  );
  useEffect(() => {
    setEnabled(new Map(version.modules.map((m) => [m.moduleKey, m.isRecommended])));
  }, [version.id, version.modules]);

  const dirty = useMemo(() => {
    const before = new Map(version.modules.map((m) => [m.moduleKey, m.isRecommended]));
    if (before.size !== enabled.size) return true;
    for (const [k, v] of enabled) if (before.get(k) !== v) return true;
    return false;
  }, [enabled, version.modules]);

  const save = useMutation({
    mutationFn: () => {
      const rows: ModuleRow[] = [...enabled.entries()].map(([moduleKey, isRecommended]) => ({
        moduleKey,
        isRecommended,
      }));
      return replaceVersionModules(version.id, rows);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) }),
        // The proactive validate query is keyed separately from the detail
        // query and nothing else refreshes it -- without this, a fixed
        // NO_MODULES issue stays reported forever and Publish never re-enables.
        qc.invalidateQueries({ queryKey: ['business-type-validate'] }),
      ]);
    },
    onError: (err) => {
      if (err instanceof AdminApiError && err.code === 'VERSION_NOT_EDITABLE') {
        // Client/server disagreed about status; a refetch renders the
        // read-only state instead of a red error.
        void qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
      }
    },
  });

  function cycle(key: string) {
    if (!isDraft) return;
    setEnabled((prev) => {
      const next = new Map(prev);
      const s = stateOf(prev, key);
      if (s === 'off') next.set(key, false);
      else if (s === 'optional') next.set(key, true);
      else next.delete(key);
      return next;
    });
  }

  const moduleIssues = issues.filter((i) => i.code === 'NO_MODULES' || i.code === 'UNKNOWN_MODULE');

  return (
    <div className="flex flex-col gap-4">
      {moduleIssues.length > 0 && (
        <ul
          role="alert"
          className="flex flex-col gap-1 rounded-control border border-danger bg-danger-pale p-3 text-body-sm text-danger-text"
        >
          {moduleIssues.map((i) => (
            <li key={i.code}>{i.message}</li>
          ))}
        </ul>
      )}

      <p className="text-body-sm text-muted">
        Click a module to cycle Off &rarr; Optional &rarr; Recommended.
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {allKeys.map((key) => {
          const s = stateOf(enabled, key);
          const known = KNOWN.has(key);
          return (
            <button
              key={key}
              type="button"
              disabled={!isDraft}
              onClick={() => cycle(key)}
              className={`flex items-center justify-between gap-2 rounded-control border p-2.5 text-left text-body-sm disabled:cursor-not-allowed disabled:opacity-disabled ${
                s === 'off' ? 'border-border bg-surface' : 'border-brand-primary bg-surface-raised'
              }`}
            >
              <span className="flex flex-col">
                <span className="text-body-sm-emphasis text-primary">{key}</span>
                {!known && (
                  <Badge
                    variant="warning"
                    className="mt-1 w-fit"
                    title="Not a known module key; publish will flag it"
                  >
                    Unknown key
                  </Badge>
                )}
              </span>
              <Badge
                variant={s === 'recommended' ? 'success' : s === 'optional' ? 'info' : 'neutral'}
              >
                {s === 'off' ? 'Off' : s === 'optional' ? 'Optional' : 'Recommended'}
              </Badge>
            </button>
          );
        })}
      </div>

      {isDraft && (
        <div className="flex items-center gap-3">
          <Button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            loading={save.isPending}
          >
            Save modules
          </Button>
          {save.isError &&
            !(
              save.error instanceof AdminApiError && save.error.code === 'VERSION_NOT_EDITABLE'
            ) && (
              <span className="text-body-sm text-danger-text">
                {save.error instanceof Error ? save.error.message : 'Could not save.'}
              </span>
            )}
        </div>
      )}
    </div>
  );
}
